import type { Result } from 'neverthrow';

import { apiRequest, type ApiError } from './client';

/**
 * Manual-sync trigger for a connected store. Posts to
 * `POST /stores/{id}/sync` which (per ADR 0019) creates a SyncJob row
 * and enqueues `{job_id}` for the sync-jobs-worker. Returns 202 with
 * the job_id immediately; the dashboard then polls `GET /sync-jobs/
 * {id}` for live progress via the SyncJobModal.
 */
export interface SyncStoreOrdersResponse {
  readonly job_id: string;
  readonly status: 'queued';
}

export function syncStoreOrders(
  connectionId: string,
): Promise<Result<SyncStoreOrdersResponse, ApiError>> {
  return apiRequest<SyncStoreOrdersResponse>({
    method: 'POST',
    path: `/stores/${encodeURIComponent(connectionId)}/sync`,
  });
}

/**
 * Self-service "Resync all orders" trigger (PR 43). Posts to
 * `POST /stores/{id}/resync` — clears the connection's `sync_cursor`
 * and enqueues a full-history SyncJob. Manual-mode connections only;
 * automatic + status modes have BC webhooks and would race the live
 * stream. Same return shape as `syncStoreOrders` so the SPA can hand
 * the job_id straight to the SyncJobModal via the same mount-from-
 * localStorage path.
 */
export function resyncStoreOrders(
  connectionId: string,
): Promise<Result<SyncStoreOrdersResponse, ApiError>> {
  return apiRequest<SyncStoreOrdersResponse>({
    method: 'POST',
    path: `/stores/${encodeURIComponent(connectionId)}/resync`,
  });
}
